<script lang="ts">
  // libs
  import Flash from "@shared/services/flashv2.js";
  import ProductContainerDetails from "@shared/filters/product_container_details.js";
  import { formatAmountString } from "@retailer/services/saved_products";

  // components
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import RemoveModal from "@retailer/svelte/Cart/RemoveModal.svelte";

  // types
  import type { SavedProductType } from "@shared/types/SavedProducts";
  import type { Product } from "@shared/types/ProductLine";

  // services
  import * as SavedProductsService from "@retailer/svelte/Cart/SavedForLater/Api.svelte";
  import { DistributorEntity, ProductEntity } from "@shared/services/event_tracking/entities";

  // props
  export let savedProduct: SavedProductType;
  export let callbacks;
  export let cartService;
  export let isMobile;
  export let showLoading: boolean = false;

  // data
  let loading: boolean = false;
  let showRemoveModal: boolean = false;

  // functions
  const moveToCartCallback = savedProducts => {
    callbacks.redoSavedProducts(savedProducts);
    Flash.now({
      message: `<p style="display:inline" class="fizz-text-strong">${savedProduct.product_line.name}</p>	
                <p style="display:inline"> was moved to cart.</p>`,
      type: "success",
      rawHtml: true,
    });
  };
  const moveToCart = (savedProduct: SavedProductType) => {
    loading = true;
    SavedProductsService.moveToCart(cartService, savedProduct, moveToCartCallback);
  };
  const doDelete = (savedProduct: SavedProductType) => {
    loading = true;
    SavedProductsService.doDelete(savedProduct, true, callbacks.redoSavedProducts);
  };
  const productContainerDetails = (product: Product) =>
    ProductContainerDetails()(product, null, null, true);
</script>

{#if !savedProduct.deleted}
  <div
    class="fizz-flex-row"
    class:fizz-inset-square-32={!isMobile}
    class:fizz-inset-square-16={isMobile}
    class:fizz-border-top={isMobile}
    data-testid="saved-product"
  >
    <ProductImage
      className="cart__item-image"
      fizz={true}
      width={50}
      height={70}
      product={savedProduct.product_line}
    />
    <div class="fizz-flex-item fizz-flex-row-8-responsive fizz-space-between">
      <div class="fizz-flex-vertical" class:fizz-half-width={!isMobile}>
        <UIRouterLink
          stateName="layout.product_detail"
          classes="fizz-text-strong notranslate fizz-text-inherit"
          params={{ productId: savedProduct.product_line_id }}
          useTrackClick
          eventName="saved_for_later_product_redirect"
          trackingParams={[
            new ProductEntity({ productId: savedProduct.product_id }),
            new DistributorEntity({ id: savedProduct.distributor_id }),
          ]}
        >
          {savedProduct.product_line.name}
        </UIRouterLink>
        <div class="fizz-text-subdued fizz-text-small">
          {productContainerDetails(savedProduct.product)}
          {#if savedProduct.sku}(SKU: {savedProduct.sku}){/if}
        </div>
      </div>
      <div class="fizz-flex-item">
        <div class="fizz-text-small fizz-flex-row fizz-stack-4" data-testid="product-total">
          <div class="fizz-text-strong fizz-inline-4">Qty:</div>
          <div>{formatAmountString(savedProduct)}</div>
        </div>
        {#if savedProduct.deleted_at}
          <div class="fizz-flex-row-4 fizz-text-negative">
            <Icon type="Error" className="fizz-icon-12" />
            <UIRouterLink
              stateName="layout.product_detail"
              classes="underline"
              params={{ productId: savedProduct.product_line.id }}
            >
              See other sizes
            </UIRouterLink>
          </div>
        {/if}
      </div>
      <div class="fizz-flex-row fizz-text-subdued fizz-text-small fizz-flex-item">
        {#if !isMobile}
          <div class="fizz-flex-item fizz-full-width" />
        {/if}
        <a
          class="fizz-text-inherit fizz-inline-16"
          data-testid="delete-saved-btn"
          class:button--disabled-link={loading}
          on:click|preventDefault={() => (showRemoveModal = true)}
          id="remove-button"
          href={"#"}
        >
          <u>Remove</u>
        </a>
        <div class="fizz-relative">
          <RemoveModal
            on:delete={() => doDelete(savedProduct)}
            bind:cartProduct={savedProduct}
            bind:show={showRemoveModal}
            location={"saved for later"}
          />
        </div>
        {#if !savedProduct.deleted_at}
          <a
            class="fizz-text-inherit"
            class:button--disabled-link={showLoading}
            on:click|preventDefault={() => moveToCart(savedProduct)}
            data-testid="move-to-cart-btn-test-hook"
            href={"#"}
          >
            <u>Move to cart</u>
          </a>
        {/if}
      </div>
    </div>
  </div>
{/if}
